import type { ErrorEvent } from "@sentry/react";

import { getHttpStatusCode } from "../getHttpStatusCode";
import { getMapVendor } from "../getMapVendor";
import { isChunkLoadError } from "../isChunkLoadError";
import { isHttpClientError } from "../isHttpClientError";
import { isLikelyBot } from "../isLikelyBot";
import { isResourceLoadError } from "../isResourceLoadError";
import { normalizeApiPath } from "../normalizeApiPath";

/**
 * Fingerprints override Sentry's default stack-trace grouping.
 *
 * They exist for error classes whose stack trace is meaningless as a grouping
 * key — either because it is identical across unrelated failures, or because it
 * shifts between deploys and splits one problem into a new issue each release.
 */
const FINGERPRINTS = {
  chunkLoad: "chunk-load-failure",
  resourceLoad: "resource-load-failed",
  httpClient: "http-client-error",
} as const;

/**
 * Groups a failed API request by endpoint and status code instead of by stack
 * trace.
 *
 * Every one of these events comes from the same `fetch()` call in
 * client-library, so the default stack-trace grouping collapses unrelated
 * failures into a single issue: resolving it for a fixed 404 gets reopened by an
 * unrelated 500 on a different endpoint. Fingerprinting on endpoint + status
 * also makes grouping immune to the minified frame changing between deploys.
 *
 * The endpoint and status are copied into tags because the status otherwise
 * lives only on the `response` context, which Sentry can't search or aggregate
 * on. The message is rewritten because the default is identical for every
 * endpoint, which makes the issue stream unreadable. Mutating it is safe: the
 * error is synthesised for reporting and never thrown through our code.
 */
const applyHttpClientGrouping = (event: ErrorEvent): ErrorEvent => {
  const endpoint = normalizeApiPath(event.request?.url || "");
  const status = getHttpStatusCode(event);
  const statusKey = status !== null ? String(status) : "unknown";

  event.fingerprint = [FINGERPRINTS.httpClient, endpoint, statusKey];
  event.tags = {
    ...event.tags,
    "http.endpoint": endpoint,
    "http.status_code": statusKey,
  };

  const httpClientValue = event.exception?.values?.find(
    (value) => value?.mechanism?.type === "http.client",
  );

  if (httpClientValue) {
    const method = event.request?.method;
    httpClientValue.value = `HTTP ${statusKey} on ${method ? `${method} ` : ""}${endpoint}`;
  }

  return event;
};

/**
 * Last chance to drop, retag or regroup an event before it leaves the browser.
 *
 * Order matters: the drop runs first, then the most specific error classes,
 * since each fingerprinting branch returns immediately.
 */
export const beforeSend = (event: ErrorEvent): ErrorEvent | null => {
  if (isLikelyBot()) return null;

  if (isHttpClientError(event)) return applyHttpClientGrouping(event);

  // Downgraded to a warning so transient chunk failures stop paging.
  if (isChunkLoadError(event)) {
    event.level = "warning";
    event.fingerprint = [FINGERPRINTS.chunkLoad];
    return event;
  }

  // Tagged rather than dropped, so map library noise can be muted by tag while
  // real bugs surfacing through those libraries stay visible.
  const mapVendor = getMapVendor(event);
  if (mapVendor) {
    event.tags = { ...event.tags, source: "map-vendor", mapVendor };
  }

  // Grouped into one issue so the volume is measurable by browser and OS. Any
  // mapVendor tag set above is preserved, which is what distinguishes a blocked
  // tile fetch from a blocked API call.
  if (isResourceLoadError(event)) {
    event.fingerprint = [FINGERPRINTS.resourceLoad];
    event.tags = {
      ...event.tags,
      source: event.tags?.source ?? FINGERPRINTS.resourceLoad,
    };
    return event;
  }

  return event;
};
